import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { FC, useEffect } from 'react';
import { Control, UseFormSetValue, useWatch } from 'react-hook-form';

import Select from '../forms/Select';
import TextField from '../forms/TextField';
import FormStack from '../forms/FormStack';
import FormGrid from '../forms/FormGrid';
import RadioButtons from '../forms/RadioButtons';
import ProfileDto from '../../dto/profiles/out/profile.dto';
import AdminProfileFieldValuesInterface from '../../interfaces/profiles/admin-profile-field-values.interface';
import { PROVINCES_OPTIONS } from '../../utils/provinces';
import {
  validateApchqMemberNumber,
  validateEmailFormat,
  validatePostalCodeFormat,
  validateRbqMemberNumber,
  validateRequired,
} from '../../utils/validations';

interface Props {
  control: Control<AdminProfileFieldValuesInterface>;
  profile: ProfileDto;
  setValue: UseFormSetValue<AdminProfileFieldValuesInterface>;
}

const AdminCompanyForm: FC<Props> = ({ control, setValue }) => {
  const isApchqMember = useWatch({ name: 'isApchqMember', control }) === 'true';
  const isRbqMember = useWatch({ name: 'isRbqMember', control }) === 'true';

  useEffect(() => {
    if (!isApchqMember) {
      setValue('apchqMemberNumber', '');
      setValue('apchqExpiryDate', '');
    }
  }, [isApchqMember, setValue]);

  useEffect(() => {
    if (!isRbqMember) {
      setValue('rbqMemberNumber', '');
      setValue('rbqExpiryDate', '');
    }
  }, [isRbqMember, setValue]);

  return (
    <FormStack>
      <FormGrid>
        <Grid item xs={12}>
          <Typography variant="overline" component="h2">
            Entreprise
          </Typography>
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            control={control}
            fullWidth
            label="Courriel du compte"
            name="email"
            rules={{
              ...validateRequired(),
              ...validateEmailFormat(),
            }}
          />
        </Grid>

        <Grid item xs={12} sm={6} />

        <Grid item xs={12} sm={6}>
          <TextField
            control={control}
            fullWidth
            label="Nom légal de l'entreprise"
            name="legalCompanyName"
            rules={validateRequired()}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            control={control}
            fullWidth
            label="Nom usuel (si applicable)"
            name="commonCompanyName"
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            control={control}
            fullWidth
            label="Adresse"
            name="address"
            rules={validateRequired()}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            control={control}
            fullWidth
            label="Ville"
            name="city"
            rules={validateRequired()}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <Select
              control={control}
              label="Province"
              name="province"
              options={PROVINCES_OPTIONS}
              rules={validateRequired()}
            />
          </FormControl>
        </Grid>

        <Grid item sm={3}>
          <TextField
            control={control}
            fullWidth
            label="Code postal"
            name="postalCode"
            placeholder="ex: G1G 1G1"
            rules={validatePostalCodeFormat()}
          />
        </Grid>

        <Grid item xs={12}>
          <RadioButtons
            control={control}
            label="Êtes-vous membre de l'APCHQ ?"
            name="isApchqMember"
            options={[
              { label: 'Oui', value: 'true' },
              { label: 'Non', value: 'false' },
            ]}
            rules={validateRequired()}
            row
          />
        </Grid>

        <>
          {isApchqMember && (
            <Grid item xs={12}>
              <TextField
                control={control}
                label="Numéro de membre APCHQ"
                name="apchqMemberNumber"
                rules={validateApchqMemberNumber(isApchqMember)}
              />
            </Grid>
          )}

          {isApchqMember && (
            <Grid item xs={12}>
              <TextField
                control={control}
                InputLabelProps={{ shrink: true }}
                label="Date d'échéance APCHQ"
                name="apchqExpiryDate"
                type="date"
              />
            </Grid>
          )}

        </>

        <Grid item xs={12}>
          <RadioButtons
            control={control}
            label="Avez-vous un numéro de licence RBQ ?"
            name="isRbqMember"
            options={[
              { label: 'Oui', value: 'true' },
              { label: 'Non', value: 'false' },
            ]}
            rules={validateRequired()}
            row
          />
        </Grid>

        {isRbqMember && (
          <Grid item xs={12}>
            <TextField
              control={control}
              label="Numéro de licence RBQ"
              name="rbqMemberNumber"
              rules={validateRbqMemberNumber(isRbqMember)}
            />
          </Grid>
        )}

        {isRbqMember && (
          <Grid item xs={12}>
            <TextField
              control={control}
              InputLabelProps={{ shrink: true }}
              label="Date d'échéance RBQ"
              name="rbqExpiryDate"
              type="date"
            />
          </Grid>
        )}

      </FormGrid>
    </FormStack>
  );
};

export default AdminCompanyForm;
